import React, { useMemo, useState, useContext, useEffect } from 'react'
import { trackEvent } from 'analytics/trackFunctions'
import { changePassword } from 'api/change-password'
import { HelpIcon, EmptyBullet, PwdTick } from 'assets/icons'

import TextInputField from 'components/input'
import PrimaryButton from 'components/primaryButton'
import { PASSWORD } from 'constants/password'

import { passwordValidation } from 'constants/storage'
import WORDING from 'constants/wording'
import { MeContext } from 'contexts/me'

import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import storage from 'utils/storage'

const isCorrect = 'text-green-500'
const isWrong = 'text-red-500'
const isDisabled = 'text-gray-600'
const isPwdIcon = {
  isOk: <PwdTick className='mr-2' />,
  isDefault: <EmptyBullet className='mr-2' />
}
const hidden = 'hidden'

const ChangePassword = () => {
  const { control, watch, handleSubmit } = useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [, setDisabled] = useState(true)

  const newPassword = watch(['newPassword', 'repeatPassword'])
  const navigate = useNavigate()
  const { me } = useContext(MeContext)

  const { PASSWORD_INFO, PASSWORD_SECURITY_INFO } = WORDING

  useEffect(() => {
    const passwordToken = sessionStorage.getItem('password_token')
    if (!passwordToken) navigate('/home', { replace: true })
  }, [])

  const obj = useMemo(() => {
    const newObj = Object.entries(PASSWORD).map((item) => [...item, isDisabled])
    if (newPassword[0]?.length > 0 && newPassword[1]?.length > 0) {
      let isAllCorrect = true
      if (newPassword[0] !== newPassword[1]) {
        newObj[0][2] = isWrong
        newObj[1][2] = hidden

        isAllCorrect = false
      } else {
        newObj[1][2] = isCorrect
        newObj[1][3] = isPwdIcon.isOk
        newObj[0][2] = hidden
      }
      if (
        !passwordValidation.uppercase.test(newPassword[0]) ||
        !passwordValidation.uppercase.test(newPassword[1])
      ) {
        newObj[2][2] = isWrong
        isAllCorrect = false
      } else {
        newObj[2][2] = isCorrect
        newObj[2][3] = isPwdIcon.isOk
      }
      if (
        !passwordValidation.number.test(newPassword[0]) ||
        !passwordValidation.number.test(newPassword[1])
      ) {
        newObj[3][2] = isWrong
        isAllCorrect = false
      } else {
        newObj[3][2] = isCorrect
        newObj[3][3] = isPwdIcon.isOk
      }
      if (
        !passwordValidation.min.test(newPassword[0]) ||
        !passwordValidation.min.test(newPassword[1])
      ) {
        newObj[4][2] = isWrong
        isAllCorrect = false
      } else {
        newObj[4][2] = isCorrect
        newObj[4][3] = isPwdIcon.isOk
      }
      if (
        !passwordValidation.lowercase.test(newPassword[0]) ||
        !passwordValidation.lowercase.test(newPassword[1])
      ) {
        newObj[5][2] = isWrong
        isAllCorrect = false
      } else {
        newObj[5][2] = isCorrect
        newObj[5][3] = isPwdIcon.isOk
      }
      newObj.isDisabled = !isAllCorrect
    } else {
      newObj.isDisabled = true
    }
    return newObj
  }, [newPassword])

  const onSubmit = () => {
    setIsLoading(true)
    changePassword({
      password: newPassword[0],
      password_confirmation: newPassword[1],
      token: storage.get('password_token'),
      email: me?.profile?.email
    })
      .then((data) => {
        setIsLoading(false)
        setDisabled(false)
        if (data.success) {
          setDisabled(true)
          navigate('contrasena-modificada', { replace: true })
        } else {
          navigate('error-contrasena-modificada')
        }
      })
      .catch((error) => {
        console.warn(error)
        navigate('error-contrasena-modificada')
        setIsLoading(false)
        setDisabled(false)
      })
  }

  return (
    <>
      <header className='w-full h-14 bg-white flex items-center justify-center font-bold'>
        <h1>Cambiar contraseña</h1>
      </header>
      <section className='md:max-w-564 md:flex md:flex-col mx-auto flex flex-col sm:justify-center px-4 w-full h-[85vh]'>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='w-full flex justify-between h-full flex-col mt-9'
        >
          <div>
            <p className='font-manrope text-xs leading-5 font-bold text-black'>
              {PASSWORD_SECURITY_INFO}{' '}
            </p>
            <Controller
              name='newPassword'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextInputField
                  {...field}
                  id='newPassword'
                  inputType='password'
                  label='Cambiar contraseña'
                  maxLength={30}
                />
              )}
            />
            <Controller
              name='repeatPassword'
              defaultValue=''
              control={control}
              render={({ field }) => (
                <TextInputField
                  {...field}
                  id='repeatPassword'
                  inputType='password'
                  label='Repetí tu nueva contraseña'
                  margins='mt-4'
                  maxLength={30}
                />
              )}
            />

            <div className='mt-4'>
              <ul>
                {obj.map((el) => {
                  const e = el[1]
                  return (
                    <div
                      className={`${el[2]} text-gray-500 font-bold font-manrope text-2xs leading-5`}
                    >
                      <li className='flex flex-row items-center'>
                        {el[3] ? isPwdIcon.isOk : isPwdIcon.isDefault}
                        <span>{e}</span>
                      </li>
                    </div>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className='w-full mt-32'>
            <div className='flex flex-row w-full justify-evenly items-center mt-7'>
              <HelpIcon variant='GREEN' />
              <div className='w-[90%]'>
                <p className='font-manrope font-bold text-black text-xs leading-5'>
                  {PASSWORD_INFO}
                </p>
              </div>
            </div>
            <div className='pb-5 w-full'>
              <PrimaryButton
                isDisabled={isLoading || obj.isDisabled}
                isLoading={isLoading}
                type='submit'
                buttonText='Cambiar contraseña'
                className='mt-5'
                primaryButtonFunction={() =>
                  trackEvent({
                    eventName: 'bille_configuracion_boton_cambiar_contrasena'
                  })
                }
              />
            </div>
          </div>
        </form>
      </section>
    </>
  )
}

export default ChangePassword
